/**
 * @module builder
 */
import type { OptionsDocument, QuoteDocument } from 'paintscout'

import cloneDeep from 'lodash/cloneDeep'
import { uuid } from '../../../util'
import { copyItem } from '../../items/copy-items/copy-items'
import { getItem } from '../../items/get-items/get-item/get-item'
import { updateItem } from '../../items/update-items/update-item'
import { createGroup } from '../create-group/create-group'
import { getFiles } from '../../files/get-files/get-files'

export function copyGroup(args: {
  quote: QuoteDocument
  options: OptionsDocument
  key: string
  calculate?: boolean
  useKey?: string
}): QuoteDocument {
  const { quote, options, key } = args
  const oldGroup = cloneDeep(quote.groups[key])
  if (!oldGroup) {
    return quote
  }

  // Keep the new keys we use to create items so we can make a group out of them
  const newKeys = []

  // Keep the child keys to filter out files we don't want to copy
  const childKeys = []

  // Copy each child item and have them added to quote
  const quoteWithGroupItems = oldGroup.children.reduce((accQuote: QuoteDocument, childKey: string) => {
    const newKey = uuid()
    newKeys.push(newKey)
    childKeys.push(childKey)
    return copyItem({ quote: accQuote, key: childKey, options, calculate: false, useKey: newKey })
  }, quote)

  // Create a new group to get files for each item
  const createdGroup = createGroup({
    quote: quoteWithGroupItems,
    clientOptions: options,
    keys: newKeys,
    section: oldGroup.section
  })

  // Filter out old item files, adding in new group files, keeping any ones that may have been added to oldgroup iteself
  const oldItemFiles = childKeys.reduce((accFiles: string[], childKey: string) => {
    const item = getItem({ quote: quoteWithGroupItems, options, key: childKey })
    return [...accFiles, ...getFiles({ quote: quoteWithGroupItems, keys: item.files }).map((file) => file.key)]
  }, [])
  const newFiles = [...createdGroup.files, ...oldGroup.files].filter((file) => !oldItemFiles.includes(file))

  // Create new group with the new keys and files, update quote
  const newGroup = {
    ...oldGroup,
    key: uuid(),
    children: newKeys,
    files: newFiles
  }
  const quoteWithGroup = updateItem({ quote: quoteWithGroupItems, options, item: newGroup })

  return quoteWithGroup
}
